import {tokens} from '@pleo-io/telescope'
import {StaticQuery, graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import OptionsToggle from '@/components/options-toggle'
import Pricing, {BillingCycle} from '@/components/pricing'
import {PricingSectionQuery} from '@/graphql-types'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import RichText, {hasRichText, Props as RichTextProps} from '@helios/shared/components/rich-text'
import {BackgroundWrapper, Section, TitleLayout} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import {PrismicData} from '@helios/shared/types/prismic-data'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

type Props = {
    title?: RichTextProps['content']
    withCtas?: boolean
    dataTestId?: string
}

type PricingSectionProps = Props & {
    content: PrismicData<PricingSectionQuery, 'allPrismicPricingTiers'>
}

export const PricingSection = ({title, content, withCtas, dataTestId}: PricingSectionProps) => {
    const [billingCycle, setBillingCycle] = React.useState<BillingCycle>(BillingCycle.Yearly)
    const billingCycleContent = content.data?.billing_cycle[0]

    const handleToggleChange = (cycle: BillingCycle) => {
        track(TrackingEvent.PricingBillingCycleActioned, {
            action: 'changed',
            selected: cycle,
        })

        setBillingCycle(cycle)
    }

    const optionsToggle = {
        name: 'billing',
        optionLeft: {
            label: billingCycleContent?.billed_monthly_label,
            value: BillingCycle.Monthly,
        },
        optionRight: {
            label: billingCycleContent?.billed_yearly_label,
            value: BillingCycle.Yearly,
        },
        legend: billingCycleContent?.legend,
    }

    const hasOptionsContent = optionsToggle.optionLeft.label && optionsToggle.optionRight.label

    return (
        <BackgroundWrapper color="muted" data-testid={dataTestId}>
            <SectionWrapper>
                {hasRichText(title) && (
                    <TitleLayout>
                        <RichText content={title} variant="heading-large" as="h2" />
                    </TitleLayout>
                )}
                {hasOptionsContent && (
                    <BillingCycleWrapper>
                        <OptionsToggle
                            {...optionsToggle}
                            value={billingCycle}
                            onChange={handleToggleChange}
                        />
                        {billingCycleContent.description && (
                            <Text color="brand" variant="paragraph-base">
                                {billingCycleContent.description}
                            </Text>
                        )}
                    </BillingCycleWrapper>
                )}
                <Pricing
                    withCtas={withCtas}
                    content={content.data}
                    mode={hasOptionsContent ? billingCycle : BillingCycle.Yearly}
                />
            </SectionWrapper>
        </BackgroundWrapper>
    )
}

const PricingSectionContainer = ({
    allPrismicPricingTiers,
    ...props
}: Props & PricingSectionQuery) => {
    const [content] = useLocalizedContent(allPrismicPricingTiers.nodes)
    return <PricingSection content={content} {...props} />
}

const query = graphql`
    query PricingSection {
        allPrismicPricingTiers {
            nodes {
                lang
                ...Pricing
            }
        }
    }
`

export default function PricingSectionQueryContainer(props: Props) {
    return (
        <StaticQuery
            query={`${query}`}
            render={(data: PricingSectionQuery) => <PricingSectionContainer {...data} {...props} />}
        />
    )
}

const SectionWrapper = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing60};
`

const BillingCycleWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing10};
    justify-items: center;

    ${mq.min('md')} {
        row-gap: ${tokens.spacing20};
    }
`
